import { ApiError, NetworkError } from "./errors";

/**
 * File download transport, kept deliberately separate from `apiClient`.
 *
 * `apiClient` is structurally JSON-only — it hardcodes a JSON Content-Type,
 * always JSON.stringify's the body and discards any non-JSON response as
 * `null`. Widening it would touch every feature and ~40 test files, so a
 * non-JSON response body gets this module instead.
 *
 * Failures still throw `ApiError` built from the admin API's error envelope,
 * so callers keep using `describeError` / `errorReason` unchanged — and a
 * request that never reached the API throws `NetworkError`, exactly as
 * apiClient's does.
 */

/**
 * Pull the filename out of a Content-Disposition header. RFC 5987's
 * `filename*=UTF-8''…` wins when present — it is the only form that survives
 * a non-ASCII project name. Returns null when the header names nothing.
 */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;

  const extended = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // A malformed percent-escape is not worth failing the download over.
      return extended[1].trim();
    }
  }

  const quoted = /filename\s*=\s*"([^"]*)"/.exec(header);
  if (quoted && quoted[1]) return quoted[1];

  const bare = /filename\s*=\s*([^;]+)/.exec(header);
  if (bare && bare[1].trim()) return bare[1].trim();

  return null;
}

/**
 * Hand a blob to the browser as a saved file, under `filename`.
 *
 * The half of a download that has nothing to do with fetching one: the config
 * page builds its profanity CSV out of a list it already holds, and going to
 * the server for bytes the page is looking at would only be a second answer
 * that could disagree with the first. It saves through this, and so does the
 * downloader below, so there is one anchor and one revoke rather than a copy
 * per caller.
 */
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    // Firefox only honours a click on an anchor that is in the document.
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Deferred by a tick: Safari has historically aborted a download whose
    // object URL was revoked in the same task as the click.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

class FileDownloader {
  private fetchImpl: typeof fetch = (...a) => fetch(...a);

  /** Same injectable seam as apiClient, so tests can drive this with msw. */
  setFetch(f: typeof fetch) {
    this.fetchImpl = f;
  }

  /**
   * GET `path` with session cookies and hand the body to the browser as a
   * saved file. Returns the filename actually used — the server's
   * Content-Disposition when it names one, else `fallbackFilename`.
   */
  async download(path: string, fallbackFilename: string): Promise<string> {
    // The second transport of the panel, so it fails in the same vocabulary as
    // apiClient: a request that never completed is a NetworkError, not the
    // bare TypeError `fetch` rejects with.
    let resp: Response;
    try {
      resp = await this.fetchImpl(path, { credentials: "include" });
    } catch (cause) {
      throw new NetworkError(cause);
    }

    if (!resp.ok) {
      // The error path *is* JSON, so decode the envelope the admin API writes
      // and rethrow it in the shape the rest of the UI already handles.
      let code = `http_${resp.status}`;
      let details: unknown;
      let message: string | undefined;
      const contentType = resp.headers.get("Content-Type") || "";
      if (contentType.startsWith("application/json")) {
        try {
          const payload = await resp.json();
          code = payload?.error?.code ?? code;
          details = payload?.error?.details;
          message = payload?.error?.message;
        } catch {
          // Body promised JSON and wasn't — keep the http_<status> code.
        }
      }
      throw new ApiError(code, resp.status, details, message);
    }

    const filename =
      filenameFromDisposition(resp.headers.get("Content-Disposition")) ??
      fallbackFilename;
    saveBlob(await resp.blob(), filename);
    return filename;
  }
}

export const fileDownloader = new FileDownloader();
